import type { PaymentMeanType } from 'digicel-kaizen-library';
import { isEmpty } from 'lodash';
import { Platform } from 'react-native';
import I18n from '../../lib/i18n';
import type { StateType } from '../reducers';

export const customerSelector = (state: StateType): ?Object => {
  const customer = state.customer?.data;

  if (isEmpty(customer)) {
    return undefined;
  }

  const getTrigramRegionName = (trigram: string) => {
    const stateOrProvinceKey = `SignUp.ChooseRegion.regionName.${trigram}`;

    return I18n.has(stateOrProvinceKey) ? I18n.t(stateOrProvinceKey) : trigram;
  };

  const billingStateOrProvince = customer.billingAddress?.stateOrProvince;
  const stateOrProvince = customer.stateOrProvince;
  const finalFullName = `${customer.givenName} ${customer.middleName ?? ''} ${customer.familyName ?? ''}`
    .replace(/\s+/g, ' ')
    .trim();
  return {
    ...customer,
    ...(billingStateOrProvince && {
      billingAddress: {
        ...customer.billingAddress,
        stateOrProvince: getTrigramRegionName(billingStateOrProvince),
      },
    }),
    ...(stateOrProvince && { stateOrProvince: getTrigramRegionName(stateOrProvince) }),
    fullName:
      customer.givenName && (customer.middleName || customer.familyName)
        ? finalFullName
        : I18n.t('profile.missingNames'),
  };
};

export const customerEmailSelector = (state: StateType): ?string => {
  const customer = customerSelector(state);
  return customer?.email;
};

export const billingAddressSelector = (state: StateType): Object => {
  const customer = customerSelector(state);
  return customer?.billingAddress || {};
};

export const hasBillingAddressSelector = (state: StateType): boolean => {
  const billingAddress = billingAddressSelector(state);
  return !!billingAddress?.postCode;
};

export const hasCustomPaymentMeanSelector = (state: StateType): boolean => {
  return !!customPaymentMeanSelector(state);
};

export const hasManuallyEditedIdSelector = (state: StateType): boolean => {
  return !!state.customer.hasEditedIdentificationInfo;
};

export const customPaymentMeanSelector = (state: StateType): ?PaymentMeanType => {
  const paymentMeans = paymentMeansSelector(state);
  if (!paymentMeans) return undefined;

  return paymentMeans
    .sort((a, b) => b.startDateTime - a.startDateTime)
    .find(paymentMeanItem => paymentMeanItem.type !== 'WALLET');
};

const paymentMeansSelector = (state: StateType) => {
  const customer = customerSelector(state);
  if (!customer) return null;
  return customer.paymentMeans;
};

export const paymentMeanByOuidSelector = (state: StateType, ouid: ?string): ?PaymentMeanType => {
  if (!ouid) {
    return null;
  }
  const paymentMeans = paymentMeansSelector(state);
  const paymentMean = paymentMeans && paymentMeans.find(paymentMean => paymentMean.ouid === ouid);
  if (!paymentMean) {
    return null;
  }
  return paymentMean;
};

export const paymentMeanTypeByOuidSelector = (state: StateType, ouid: ?string): ?string => {
  const paymentMean = paymentMeanByOuidSelector(state, ouid);
  if (!paymentMean) {
    return null;
  }
  return paymentMean.type;
};

export const paymentMeanCharacteristicsByOuidSelector = (state: StateType, ouid: ?string): ?Object => {
  const paymentMean = paymentMeanByOuidSelector(state, ouid);
  if (!paymentMean) {
    return null;
  }
  return paymentMean.characteristics;
};

export const balanceSelector = (state: StateType): ?number => {
  const customer = customerSelector(state);
  if (!customer) return null;
  return customer.wallet?.amount;
};

export const customerIdentificationSelector = (state: StateType): ?Object => {
  const customer = customerSelector(state);
  return customer?.identification;
};

export const hasCustomerIdentificationSelector = (state: StateType): boolean => {
  const identification = customerIdentificationSelector(state);
  return !isEmpty(identification);
};

export const customerOuidSelector = (state: StateType): ?string => {
  const customer = customerSelector(state);
  return customer?.ouid;
};

export const billingAccountOuidSelector = (state: StateType): ?string => {
  const customer = customerSelector(state);
  return customer?.billingAccounts?.[0]?.ouid;
};

export const billingAccountsOuidSelector = (state: StateType): ?Array<string> => {
  const customer = customerSelector(state);
  if (!customer) return null;

  const billingAccounts = customer.billingAccounts;
  if (!billingAccounts) return null;

  return billingAccounts.map(billing => billing.ouid);
};

export const customerExternalIdSelector = (state: StateType): ?string => {
  const customer = customerSelector(state);
  if (!customer) return null;
  return customer.externalId;
};

export const customerDeviceIdSelector = (state: StateType): ?string => {
  const externalId = customerExternalIdSelector(state);
  if (!externalId) return null;
  return `${Platform.OS}-${externalId}`;
};

export const customerNationalitySelector = (state: StateType): ?string => {
  const customer = customerSelector(state);
  return customer?.nationality;
};

export const isMalaysianSelector = (state: StateType): boolean => {
  const nat = customerNationalitySelector(state);
  return !!nat && nat === 'MYS';
};

export const customerStreetsSelector = (state: StateType): Object => {
  const customer = customerSelector(state);
  return {
    street1: customer?.street1,
    street2: customer?.street2,
  };
};

export const customerPostCodeSelector = (state: StateType): ?string => {
  const customer = customerSelector(state);
  return customer?.postCode;
};

export const customerCitySelector = (state: StateType): ?string => {
  const customer = customerSelector(state);
  return customer?.city;
};

export const customerCountrySelector = (state: StateType): ?string => {
  const customer = customerSelector(state);
  return customer?.country;
};

export const customerStateOrProvinceSelector = (state: StateType): ?string => {
  const customer = customerSelector(state);
  return customer?.stateOrProvince;
};

export const customerFullNameSelector = (state: StateType): ?string => {
  const customer = customerSelector(state);
  return customer?.fullName;
};

export const customerGivenNameSelector = (state: StateType): ?string => {
  const customer = customerSelector(state);
  return customer?.givenName;
};

export const customerTaxExemptionsSelector = (state: StateType): ?Array<Object> => {
  const customer = customerSelector(state);
  return customer?.taxExemption;
};

export const customerOptionCommercialSelector = (
  state: StateType
): ?{
  optionInternalCommercial: boolean,
  optionExternalCommercial: boolean,
} => {
  const customer = customerSelector(state);
  return {
    optionInternalCommercial: !!customer?.login?.customerPreference?.characteristics?.optionInternalCommercial,
    optionExternalCommercial: !!customer?.login?.customerPreference?.characteristics?.optionExternalCommercial,
  };
};

export const isExemptedFromTaxSelector = (state: StateType): boolean => {
  if (isMalaysianSelector(state) || !customerNationalitySelector(state)) {
    return true;
  }
  const customerTaxExemptions = customerTaxExemptionsSelector(state);
  if (!customerTaxExemptions || !customerTaxExemptions.length) {
    return false;
  }
  const now = Date.now();
  return customerTaxExemptions.some(
    taxExemption =>
      taxExemption.issuingJurisdiction === 'SST' &&
      taxExemption.startDateTime < now &&
      (!taxExemption.endDateTime || taxExemption.endDateTime > now)
  );
};

export const customerFailureStatusSelector = (state: StateType): boolean => state.customer.failure;

export const customerLatestIdentificationSelector = (state: StateType): Object => {
  const customer = customerSelector(state);
  const identificationToUse = customer?.individual?.identifications
    ?.filter(identification =>
      ['PASSPORT', 'IDENTITY_CARD', 'RESIDENT_CARD'].includes(identification.identificationType)
    )
    .find(identification => !identification.endDateTime);
  return identificationToUse;
};
